/**
 * Canonical list of tables and columns excluded from Zero replication.
 *
 * Consumed by:
 *  - setupDatabaseConstructs.ts (builds the Postgres publication)
 *  - drizzle-zero.config.ts (derives the client schema at module load)
 *  - isNonReplicatedField (UI-side guard for FieldTarget)
 *
 * excludedTables / secretColumns / pendingDropColumns drop from both the
 * client schema and publication. clientSchemaIgnoredTables/Columns drop only
 * from the client schema, for the first deploy of a Zero replication
 * contraction.
 */

import type { FieldTarget } from "../shared/connectors/types";

export const excludedTables = new Set([
  "session",
  "account",
  "verification",
  "execution_claims",
  "execution_history",
  "execution_history_files",
  "workflow_run_status_updates",
  "step_counter_expired_slots",
  "step_counter_slots",
  "workflow_run_daily_counter_slots",
  "volume_write_locks",
  "tunnel_repo_creds",
  "device_authorizations",
  "oauth_auth_code",
  // Server-side only: monitoring reads the ai_usage_daily rollup, so raw
  // usage records never reach clients or the replica.
  "ai_usage_records",
  // Git smart-HTTP server state: refs and raw git objects, served only over
  // the git protocol.
  "space_git_refs",
  "space_git_objects",
]);

export const secretColumns: Record<string, Set<string>> = {
  tenant_keys: new Set(["wrapped_private_key", "wrapped_symmetric_key"]),
  sso_provider: new Set([
    "oidc_config",
    "saml_config",
    "scim_token_hash",
    "scim_request_count",
    "scim_last_request",
  ]),
  oauth_connections: new Set([
    "client_secret",
    "access_token",
    "refresh_token",
  ]),
  connected_apps: new Set(["secrets"]),
  ai_providers: new Set(["api_key", "ca_certificate"]),
  apikey: new Set([
    "key",
    "refill_interval",
    "refill_amount",
    "last_refill_at",
    "rate_limit_enabled",
    "rate_limit_time_window",
    "rate_limit_max",
    "request_count",
    "remaining",
    "permissions",
    "metadata",
  ]),
  oauth_grant: new Set(["refresh_token_hash"]),
};

// Columns staged for DROP COLUMN. Same Zero effect as secretColumns (out of
// client schema and publication), but for ordinary columns — not secrets —
// after a clientSchemaIgnoredColumns soak. Clear the entry in the DROP PR.
export const pendingDropColumns: Record<string, Set<string>> = {};

// First deploy: stop new clients declaring these tables/columns, while keeping
// them in the publication for stale clients. Next deploy: move them into
// excludedTables, secretColumns, or pendingDropColumns.
export const clientSchemaIgnoredTables = new Set<string>([
  "workflow_stickers",
  "workflow_notes",
]);

export const clientSchemaIgnoredColumns: Record<string, Set<string>> = {
  execution_stats: new Set(["egress_bytes", "ingress_bytes"]),
};

export function isPublicationExcludedColumn(
  table: string,
  column: string
): boolean {
  return (
    (secretColumns[table]?.has(column) ?? false) ||
    (pendingDropColumns[table]?.has(column) ?? false)
  );
}

export function isNonReplicatedField(target: FieldTarget): boolean {
  return (
    target.kind === "secrets" ||
    (target.kind === "oauthConnection" && target.field === "clientSecret")
  );
}

// tenant_id is being rolled out to every table (techplan §4b). The newly added
// columns are nullable; pre-existing tenant_id columns are NOT NULL and stay
// replicated. Promoting a nullable tenant_id into the Zero publication re-copies
// that table into the replica, so we sync it table by table — smallest first,
// busiest tables last — to bound each deploy's re-bootstrap. A table is synced
// once it appears in TENANT_ID_SYNCED_TABLES; until then its nullable tenant_id
// stays out of both the publication and the client schema.
const PENDING_TENANT_ID_COLUMN = "tenant_id";

// Chunk 1: every Zero-synced table whose production row count is small (verified
// ≤ ~3.3K rows on both the original and demo stacks, June 2026) — a low-risk
// first re-bootstrap. This deliberately includes RBAC (roles/role_permissions/
// role_grants/user_groups, essentially empty in prod — read-hot for authz, but
// that doesn't affect the re-bootstrap copy) and the N×M/per-branch tables
// (space_user_stats, workflow_views, git_branch_cursors, branch_model_preferences,
// user_branch_presence), which are write-hot but tiny. Deferred to later tiers
// (techplan §4f): tables that grow large — executions (6.4M), execution_results
// (6.2M), workflow_runs (866K), and the mid-tier (chat_entries, commit_files,
// tool_calls, blobs, messages, commits, chats, …). Keys are Postgres table names.
export const TENANT_ID_SYNCED_TABLES = new Set<string>([
  "feature_flags",
  "commit_schedules",
  "space_git_remotes",
  "space_volumes",
  "personal_volumes",
  "user_preferences",
  "workflows",
  "designs",
  "oauth_connections",
  "workflow_connected_apps",
  "workflow_notes",
  "workflow_stickers",
  "workflow_favorites",
  "roles",
  "role_permissions",
  "role_grants",
  "user_groups",
  "space_user_stats",
  "workflow_views",
  "user_branch_presence",
  "git_branch_cursors",
  "branch_model_preferences",
  // Chunk 2: workflow/branch structure plus the chat shell and per-branch
  // bookkeeping that share the same write paths (verified ≤ ~5.8K chats, ~2.2K
  // branches, ~5.6K commits, ~2.1K branch_contributors, ~2.4K user_actions on
  // both stacks, June 2026). Deferred to later tiers: the chat message domain
  // (chat_entries, tool_calls, messages, …) and the giants (executions,
  // execution_results, workflow_runs).
  "workflow_branches",
  "workflow_commits",
  "chats",
  "branch_contributors",
  "user_actions",
  // Chunk 3: the commit content tables that flow through commitFiles.ts —
  // tenantId is threaded through its write Ctx and stamped at every insert.
  // blobs is content-addressed and deduped, so a hash is owned by the first
  // tenant to write it; commit_connected_apps has no live write path (legacy
  // backfill only) so it is backfilled and constrained without stamping.
  "blobs",
  "commit_files",
  "commit_routes",
  "commit_links",
  "commit_connected_apps",
  // Chunk 4: the chat/AI message domain. chat_entries is the parent row; the
  // rest are its 1:1 sub-tables keyed by entryId. tenantId is stamped from
  // ctx.tenantId in the shared mutators and resolved per-handle in the AI
  // persistence hub (background writers with no ctx). personal_chat_connected_apps
  // has no live write path (superseded by connected_app_usages), so it only needs
  // backfilling later. Still deferred to chunk 5/6: attachments and the giants
  // (executions, execution_results, workflow_runs).
  "chat_entries",
  "user_messages",
  "assistant_messages",
  "tool_calls",
  "assistant_reasonings",
  "system_messages",
  "personal_chats",
  "personal_chat_connected_apps",
  // Chunk 5: the remaining mid/small tables. attachments is stamped from
  // ctx.tenantId in the chat mutators; connection_claims from resolveTenantId on
  // the public-API write. build_caches has no tenant_id (platform CAS index).
  // step_counters is written only by the increment_step_counter trigger, which
  // already stamps w.tenant_id. Still deferred to chunk 6: the giants
  // (executions, execution_results, workflow_runs).
  "attachments",
  "connection_claims",
  // Chunk 6: the giants — workflow_runs (866K), execution_results (6.2M), and
  // executions (6.4M) rows in prod, June 2026. All power core run/execution UI
  // (WorkflowRunsPanel, RunWaterfall, StepExecutions, ExecutionGraph via
  // loadWorkflowRun/loadBranchWorkflowRuns and the executions.result relation),
  // so they can't be left out of the publication. tenant_id is already stamped
  // at every insert site (createRunExecution.ts, executions/create.ts,
  // workerServer.ts, seed.ts); this promotion is sync-only — backfill of
  // existing NULL rows and NOT NULL follow once each table's re-bootstrap is
  // confirmed healthy. workflow_runs has since been backfilled and made
  // NOT NULL; execution_results and executions are still pending.
  "workflow_runs",
  // Chunk 7: commit_email_addresses (Zero-synced via commitFiles.syncFacets) and
  // step_test_specs (orchestrator completion write only; tenant_id stamped in
  // workerServer.ts). execution_files and volume_write_locks stay in
  // excludedTables — server-side population only (api/executions/create.ts,
  // orchestrator/executionClaims.ts); no publication re-bootstrap.
  "commit_email_addresses",
  "step_test_specs",
]);

export function isReplicatedColumn(
  table: string,
  column: string,
  nullable: boolean
): boolean {
  if (column === PENDING_TENANT_ID_COLUMN && nullable) {
    return TENANT_ID_SYNCED_TABLES.has(table);
  }
  return true;
}
